import { useEffect, useRef, useState } from "react";
import Joyride, {
  EVENTS,
  STATUS,
  type CallBackProps,
  type Step,
  type TooltipRenderProps,
} from "react-joyride";
import { datadogRum } from "@datadog/browser-rum";
import Title from "../UI/Title";
import Text from "../UI/Text";
import Button from "../UI/Button";
import useOnboarding from "../../hooks/use-onboarding";
import { useToast } from "../UI/Toast/useToast";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import { RumActions } from "../../utils/constants/rum_actions.js";
import styles from "./GuidedTour.module.css";

export interface GuideStep {
  target: string;
  title: string;
  content: string;
}

interface GuidedTourProps {
  /** Identifiant persisté dans onboarding.seen_guides : le tour ne se joue qu'une fois. */
  guideId: string;
  steps: GuideStep[];
  /** Condition supplémentaire de lancement (ex. données chargées). */
  run?: boolean;
  /** Appelé à la fin ; finished=false si l'utilisateur a passé le guide. */
  onFinish?: (finished: boolean) => void;
}

function TourTooltip({
  index,
  size,
  step,
  primaryProps,
  skipProps,
  isLastStep,
  tooltipProps,
}: TooltipRenderProps) {
  return (
    <div className={styles.tooltip} {...tooltipProps}>
      <div className={styles.body}>
        <Title as="div" variant="deca" weight="bold">
          {step.title}
        </Title>
        <Text>{step.content}</Text>
      </div>
      <div className={styles.footer}>
        <Button variant="tertiary" size="sm" {...skipProps}>
          Passer le guide
        </Button>
        <Text as="span" variant="deci" className={styles.count}>
          {index + 1} sur {size}
        </Text>
        <Button variant="primary" size="sm" {...primaryProps}>
          {isLastStep ? "Terminer" : "Suivant"}
        </Button>
      </div>
    </div>
  );
}

export default function GuidedTour({
  guideId,
  steps,
  run = true,
  onFinish,
}: GuidedTourProps) {
  const { hasSeenGuide, markGuideSeen, isLoading } = useOnboarding();
  const toast = useToast();
  const [dismissed, setDismissed] = useState(false);
  const startedLogged = useRef(false);
  const finishHandled = useRef(false);
  const stepShown = useRef(false);

  const shouldRun = !dismissed && !isLoading && run && !hasSeenGuide(guideId);

  useEffect(() => {
    if (shouldRun && !startedLogged.current) {
      startedLogged.current = true;
      datadogRum.addAction(RumActions.GUIDE_STARTED, { guide: guideId });
    }
  }, [shouldRun, guideId]);

  if (!shouldRun) {
    return null;
  }

  const joyrideSteps: Step[] = steps.map((step) => ({
    ...step,
    disableBeacon: true,
  }));

  async function handleCallback(data: CallBackProps) {
    if (data.type === EVENTS.TOOLTIP) {
      stepShown.current = true;
    }

    const isTerminal =
      data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED;
    // On ne traite la fin qu'une seule fois (Joyride émet plusieurs callbacks terminaux) et
    // uniquement si une étape a réellement été affichée : si la cible est introuvable, Joyride
    // termine le tour sans rien montrer et on ne doit pas le marquer vu — il rejouera quand la
    // cible existera.
    if (!isTerminal || finishHandled.current || !stepShown.current) {
      return;
    }
    finishHandled.current = true;

    const finished = data.status === STATUS.FINISHED;
    datadogRum.addAction(
      finished ? RumActions.GUIDE_COMPLETED : RumActions.GUIDE_SKIPPED,
      {
        guide: guideId,
        step: data.index + 1,
        total: steps.length,
      },
    );
    setDismissed(true);
    try {
      await markGuideSeen(guideId);
    } catch (error) {
      // Le guide est déjà masqué localement : sans persistance il rejouera
      // au prochain chargement, d'où le signalement.
      toast.error({
        title: "Impossible d'enregistrer la fin du guide",
        description: handleAPIError(error),
      });
    }
    onFinish?.(finished);
  }

  return (
    <Joyride
      steps={joyrideSteps}
      run
      continuous
      showSkipButton
      disableOverlayClose
      scrollOffset={120}
      tooltipComponent={TourTooltip}
      callback={handleCallback}
      styles={{
        options: {
          // --color-neutral-900 à 50 %, aligné sur le backdrop de ModalDialog
          overlayColor: "rgba(61, 61, 61, 0.5)",
          // --z-tooltip
          zIndex: 500,
        },
      }}
    />
  );
}
